import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { formatDistanceToNow } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { ReleaseAnalytics } from '../../api/hooks/updates/useReleaseQuery';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ReleaseAnalyticsDashboardProps {
  analytics: ReleaseAnalytics;
}

export const ReleaseAnalyticsDashboard: React.FC<ReleaseAnalyticsDashboardProps> = ({ analytics }) => {
  const navigate = useNavigate();
  // Prepare data for update trends chart
  const updateTrendsData = {
    labels: analytics.updateTrends.map(d => d.date),
    datasets: [
      {
        label: 'Total Checks',
        data: analytics.updateTrends.map(d => d.totalChecks),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.5)',
      },
      {
        label: 'Unique Machines',
        data: analytics.updateTrends.map(d => d.uniqueMachines),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
      },
    ],
  };

  // Prepare data for download stats
  const downloadStatsData = {
    labels: analytics.releaseStats.slice(0, 10).map(r => `v${r.version}`),
    datasets: [
      {
        label: 'Successful Downloads',
        data: analytics.releaseStats.slice(0, 10).map(r => r.completedDownloads),
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
      },
      {
        label: 'Failed Downloads',
        data: analytics.releaseStats.slice(0, 10).map(r => r.failedDownloads),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Active Machines (30d)</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {analytics.updateTrends.reduce((sum, d) => Math.max(sum, d.uniqueMachines), 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Downloads</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            {analytics.releaseStats.reduce((sum, r) => sum + r.totalDownloads, 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Latest Release</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900">
            v{analytics.releaseStats[0]?.version || 'N/A'}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Update Check Trends (30 Days)</h3>
          <Line data={updateTrendsData} options={chartOptions} />
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Download Statistics by Version</h3>
          <Bar data={downloadStatsData} options={chartOptions} />
        </div>
      </div>

      {/* Active Machines Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Recently Active Machines</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Machine ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Seen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Events
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.machineActivity.slice(0, 10).map((machine) => (
                <tr key={machine.displayId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <button
                      onClick={() => navigate(`/machines/${machine.displayId}`)}
                      className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                    >
                      {machine.displayId}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {machine.platform} ({machine.architecture})
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {machine.lastVersion || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDistanceToNow(new Date(machine.lastSeen), { addSuffix: true })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {machine.totalEvents}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Release Performance */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Release Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Downloads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.releaseStats.map((release) => {
                const successRate = release.totalDownloads > 0
                  ? ((release.completedDownloads / release.totalDownloads) * 100).toFixed(1)
                  : '0';
                
                return (
                  <tr key={release.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      v{release.version}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        release.channel === 'stable' ? 'bg-green-100 text-green-800' :
                        release.channel === 'beta' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {release.channel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {release.platform} ({release.architecture})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {release.uniqueDownloads}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`${
                        parseFloat(successRate) > 95 ? 'text-green-600' :
                        parseFloat(successRate) > 80 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {successRate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};